import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationPipe } from '@abp/ng.core';
import { TooltipModule } from 'primeng/tooltip';
import { QueueDto } from '../../models/queue.dto';
import { AgentRoutingMethod } from '../../models/agent-routing-method.enum';
import { CallPriority } from '../../models/call-priority.enum';
import { QueuePriority } from '../../models/queue-priority.enum';
import { DeclineReasonItemDto } from 'src/core/models/decline-reason/decline-reason-item.dto';
import { DEFAULT_SLA_TARGET_SECONDS } from '../../models/queue-sla.constant';

const MAX_INLINE_DECLINE_REASONS = 3;

const WRAP_UP_CODES_INLINE_LIMIT = 3;

@Component({
  selector: 'app-queue-card',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, TooltipModule],
  templateUrl: './queue-card.component.html',
  styleUrl: './queue-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueueCardComponent {
  @Input({ required: true }) queue!: QueueDto;
  @Output() cardClick = new EventEmitter<QueueDto>();

  get routingLabel(): string {
    switch (this.queue.agentRoutingMethod) {
      case AgentRoutingMethod.LongestIdleTime:
        return 'Settings::Queues:RoutingMethod:LongestIdleTime:Title';
      default:
        return 'Settings::Queues:RoutingMethod:LongestIdleTime:Title';
    }
  }

  get priorityLabel(): string {
    switch (this.queue.priority) {
      case QueuePriority.Low:
        return 'Queue::QueuePriority:Low';
      case QueuePriority.High:
        return 'Queue::QueuePriority:High';
      default:
        return 'Queue::QueuePriority:Medium';
    }
  }

  get callPriorityLabel(): string {
    switch (this.queue.callPriority) {
      case CallPriority.Fifo:
        return 'FIFO';
      default:
        return 'FIFO';
    }
  }

  get maxResponseTime(): number {
    return this.queue.maxResponseTime ?? 15;
  }

  get slaTargetSeconds(): number {
    return this.queue.slaTargetSeconds ?? DEFAULT_SLA_TARGET_SECONDS;
  }

  getWrapUpCodesInline(): { id: number; name: string }[] {
    return this.queue.wrapUpCodes?.slice(0, WRAP_UP_CODES_INLINE_LIMIT) ?? [];
  }

  hasWrapUpCodesOverflow(): boolean {
    return (this.queue.wrapUpCodes?.length ?? 0) > WRAP_UP_CODES_INLINE_LIMIT;
  }

  getWrapUpCodesOverflowCount(): number {
    return Math.max(0, (this.queue.wrapUpCodes?.length ?? 0) - WRAP_UP_CODES_INLINE_LIMIT);
  }

  getWrapUpCodesOverflowTooltip(): string {
    return (this.queue.wrapUpCodes ?? [])
      .slice(WRAP_UP_CODES_INLINE_LIMIT)
      .map(code => code.name)
      .join('\n');
  }
  
  getDeclineReasonsInline(reasons: DeclineReasonItemDto[]): DeclineReasonItemDto[] {
    return reasons?.slice(0, MAX_INLINE_DECLINE_REASONS) ?? [];
  }

  hasDeclineReasonOverflow(reasons: DeclineReasonItemDto[]): boolean {
    return (reasons?.length ?? 0) > MAX_INLINE_DECLINE_REASONS;
  }

  getDeclineReasonOverflowCount(reasons: DeclineReasonItemDto[]): number {
    return (reasons?.length ?? 0) - MAX_INLINE_DECLINE_REASONS;
  }

  getDeclineReasonsTooltip(reasons: DeclineReasonItemDto[]): string {
    return reasons?.slice(MAX_INLINE_DECLINE_REASONS).map(r => r.name).join('\n') ?? '';
  }

  onClick(): void {
    this.cardClick.emit(this.queue);
  }
}
